import { createAsyncThunk } from "@reduxjs/toolkit";
import { apiUrlV1 } from "../../config/config";
import { ApiQueryParams, InterfaceProfile, ResponseObjectList } from "../../entities/global";
import { CustomEntity, ResponseCustomEntity, V3GameProfile } from "../../entities/customGame";
import { agent, encodeGetParams } from "../../api";
import { addNotification } from "../Notification/notificateStore";
import { handlerNeedUpdate, handlerNeedUpdateProfiles } from "./v3GamesTableStore";

// https://gamixter-backend.ngrok.dev/v3/organization/game/list?page=1&limit=1
const url = apiUrlV1 + "/v3/organization/game/"

/**
 * Same read as getCustomList, but deliberately WITHOUT reducer cases — the result is returned
 * to the caller and never lands in the shared slice. Needed when a screen already renders one
 * scoped list (e.g. a profile's missions) and has to look up another (the org-wide catalog)
 * without wiping what is on screen.
 */
export const fetchCustomListDetached = createAsyncThunk(
  'v3Game/get-custom-list-detached',
  async (params: ApiQueryParams): Promise<CustomEntity[]> => {
    const query = encodeGetParams({ ...params });
    const response = await agent.get(`${url}list${query}`);
    if (!response.ok) {
      throw Error('Request is not found');
    }
    const data = (await response.json()) as ResponseObjectList<CustomEntity[]>;
    return data?.data ?? [];
  },
);

export const getCustomList = createAsyncThunk(
  'v3Game/get-custom-list',
  async (params: ApiQueryParams): Promise<ResponseObjectList<CustomEntity[]>> => {
    try {
      const query = encodeGetParams({ ...params })
      const response = await agent.get(`${url}list${query}`);
      if (response.ok) {
        const data = await response.json() as ResponseObjectList<CustomEntity[]>;
        return data;
      }
      if (!response.ok) {
        throw Error("No response");
      }
    } catch (error) {
      throw Error("Request is not found");
    }
    return {
      limit: 250,
      page: 1,
      pages: 1,
      total: 0,
      data: []
    };
  }
)

export const deleteV3GameEntity = createAsyncThunk(
  'v3Game/delete-entity',
  async (params: { id: number; profileId?: number }, thunksApi) => {
    try {
      const query = params.profileId ? encodeGetParams({ profileId: params.profileId }) : '';
      const response = await agent.delete(`${apiUrlV1 + "/v3/organization/game/"}${params.id}${query}`)
      if (response.ok) {
        thunksApi.dispatch(addNotification({ status: 'success', text: "Entity deleted" }))
        thunksApi.dispatch(handlerNeedUpdate())
      }
      if (!response.ok) {
        throw Error("No response");
      }
    } catch (error) {
      throw Error("Request is not found");
    }

  }
)

export const postCustom = createAsyncThunk(
  'v3Game/post-custom',
  async (params: ResponseCustomEntity, thunksApi) => {
    try {
      const response = await agent.post(`${url}`, {
        body: JSON.stringify(params)
      })
      if (response.ok) {
        const data = await response.json();
        thunksApi.dispatch(handlerNeedUpdate())
        thunksApi.dispatch(
          addNotification({ status: "success", text: "Successfully saved" })
        );
        return data;
      }
      if (!response.ok) {
        throw Error("No response");
      }
    } catch (error) {
      throw Error("Request is not found");
    }
    return {
      limit: 250,
      page: 1,
      pages: 1,
      total: 0,
      data: []
    };
  }
)

export const getV3GameEntity = createAsyncThunk(
  'v3Game/get-entity',
  async (params: { id: number }) => {
    try {
      const response = await agent.get(`${url}${params.id}`);

      if (response.ok) {
        const data = await response.json();
        return data;
      }
      if (!response.ok) {
        throw Error("No response");
      }
    } catch (error) {
      throw Error("Request is not found");
    }
  }
)

export const patchV3GameEntity = createAsyncThunk(
  'v3Game/patch-custom',
  async (params: { data: CustomEntity, id: number }, thunksApi) => {
    try {
      const response = await agent.patch(`${url}${params.id}`, {
        body: JSON.stringify(params.data)
      })
      if (response.ok) {
        thunksApi.dispatch(handlerNeedUpdate())
        thunksApi.dispatch(
          addNotification({ status: "success", text: "Successfully saved" })
        );
        const freshData = await thunksApi.dispatch(getV3GameEntity({ id: params.id })).unwrap();
        return freshData;
      }
      if (!response.ok) {
        throw Error("No response");
      }
    } catch (error) {
      throw Error("Request is not found");
    }
  }
)


export const getV3GameTags = createAsyncThunk(
  'v3Game/get-tags',
  async (params: { game: string }) => {
    try {
      const query = encodeGetParams({ ...params })
      const response = await agent.get(`${apiUrlV1}/organization/game/tags${query}`)
      if (response.ok) {
        const body = await response.json();
        return body;
      }
    } catch (error) {

    }
  }
)

// getCasesProfiles
export const getV3GameProfiles = createAsyncThunk(
  'v3Game/get-profile-list',
  async (params: { meta?: ApiQueryParams, systemName: string }): Promise<ResponseObjectList<V3GameProfile[]>> => {
    try {
      const query = encodeGetParams({ ...params.meta });
      const response = await agent.get(`${apiUrlV1}/v3/organization/game/profile/read/${params.systemName}/list${query}`);

      if (response.ok) {
        const body = (await response.json()) as ResponseObjectList<V3GameProfile[]>;

        return body;
      }
      if (!response.ok) {
        throw Error("No response");
      }
    } catch (error) {
      throw Error("Request is not found");
    }

    return {
      limit: 250,
      page: 1,
      pages: 1,
      total: 0,
      data: [],
    };
  }
)

//Profile
export const getV3GameProfile = createAsyncThunk(
  'v3Game/get-profile-by-id',
  async (param: { id: number }): Promise<InterfaceProfile> => {
    try {
      const response = await agent.get(`${apiUrlV1}/v3/organization/game/profile/read/${param.id}`)
      if (response.ok) {
        const data = await response.json() as InterfaceProfile;
        return data;
      }
      throw Error("No response");
    } catch (error) {
      throw Error("Request is not found");
    }
  }
)


export const postV3GameProfile = createAsyncThunk(
  'v3Game/post-profile-by-id',
  async (param: { data: any, systemName: string }, thunksApi): Promise<InterfaceProfile> => {
    const v3ProfileBody = {
      ...param.data,
      isSortMode: false,
      isMultiGame: true,
      game: param.systemName
    }
    const response = await agent.post(`${apiUrlV1}/v3/organization/game/profile/create/from-global-settings`, {
      body: JSON.stringify(v3ProfileBody)
    })
    if (response.ok) {
      const data = await response.json() as InterfaceProfile;
      thunksApi.dispatch(addNotification({ status: "success", text: "Successfully saved" }));
      thunksApi.dispatch(handlerNeedUpdateProfiles());
      return data;
    }
    const result = await response.json() as { message: string | string[] }
    const msg = Array.isArray(result.message) ? result.message.join(', ') : result.message
    thunksApi.dispatch(addNotification({ status: 'error', text: msg }))
    throw Error(msg);
  }
)

export const patchV3GameProfile = createAsyncThunk(
  'v3Game/patch-profile-by-id',
  async (param: { data: any, id: number }, thunksApi): Promise<InterfaceProfile> => {
    try {
      const response = await agent.patch(`${apiUrlV1}/v3/organization/game/profile/update/from-config-part/${param.id}`, {
        body: JSON.stringify(param.data)
      })
      if (response.ok) {
        const data = await response.json() as InterfaceProfile;
        thunksApi.dispatch(
          addNotification({ status: "success", text: "Successfully saved" })
        );
        thunksApi.dispatch(handlerNeedUpdateProfiles());

        return data;
      }
      if (!response.ok) {
        const data = await response.json() as { message: string[] | string };
        if (typeof (data.message) === 'object' && data.message.length > 0) {
          data.message.forEach(element => {
            thunksApi.dispatch(
              addNotification({ status: "error", text: element })
            );
          });
        }
        if (typeof (data.message) === 'string') {
          thunksApi.dispatch(
            addNotification({ status: "error", text: data.message })
          );
        }
      }
      throw Error("No response");
    } catch (error) {
      throw Error("Request is not found");
    }
  }
)

export const deleteV3GameProfile = createAsyncThunk(
  'v3Game/delete-v3game-profile',
  async (params: { id: number }, thunksApi) => {
    try {
      // The profile takes its own settings with it (shared missions/boxes/banners stay).
      const response = await agent.delete(`${apiUrlV1}/v3/organization/game/profile/delete/${params.id}?withOwnSettings=true`)
      if (response.ok) {
        thunksApi.dispatch(handlerNeedUpdateProfiles());
      }
      if (!response.ok) {
        const result = await response.json() as { message: string }
        thunksApi.dispatch(addNotification({ status: 'error', text: result.message }))
        throw result.message;
      }
    } catch (error) {
      throw Error("Request is not found");
    }
  }
)

/** Copies a profile with its games; the copy comes back unpublished and without a trigger. */
export const copyV3GameProfile = createAsyncThunk(
  'v3Game/copy-v3game-profile',
  async (params: { id: number; name?: string }, thunksApi) => {
    const response = await agent.post(`${apiUrlV1}/v3/organization/game/profile/create/${params.id}/copy`, {
      body: JSON.stringify(params.name ? { name: params.name } : {}),
    });
    const result = await response.json();
    if (!response.ok) {
      thunksApi.dispatch(addNotification({ status: 'error', text: result?.message ?? 'Profile was not copied' }));
      throw new Error(result?.message ?? 'Profile was not copied');
    }
    thunksApi.dispatch(handlerNeedUpdateProfiles());
    return result as { id: number; name?: string };
  }
)

export const patchV3GameEntityEnabled = createAsyncThunk(
  'v3Game/patch-entity-enabled',
  async (params: { id: number; enabled: boolean }, thunksApi) => {
    try {
      const response = await agent.patch(`${url}${params.id}`, {
        body: JSON.stringify({ enabled: params.enabled })
      })
      if (response.ok) {
        return await response.json() as CustomEntity;
      }
      if (!response.ok) {
        throw Error("No response");
      }
    } catch (error) {
      throw Error("Request is not found");
    }
  }
)

export const patchV3GameProfileInTable = createAsyncThunk(
  'v3Game/patch-profile-in-table',
  async (param: { data: any, id: number }, thunksApi): Promise<InterfaceProfile> => {
    try {
      const response = await agent.patch(`${apiUrlV1}/v3/organization/game/profile/update/from-config-part/${param.id}`, {
        body: JSON.stringify(param.data)
      })
      if (response.ok) {
        const data = await response.json() as InterfaceProfile;
        thunksApi.dispatch(
          addNotification({ status: "success", text: "Successfully saved" })
        );
        // thunksApi.dispatch(handlerNeedUpdateProfiles());

        return data;
      }
      if (!response.ok) {
        const data = await response.json() as { message: string[] | string };
        if (typeof (data.message) === 'object' && data.message.length > 0) {
          data.message.forEach(element => {
            thunksApi.dispatch(
              addNotification({ status: "error", text: element })
            );
          });
        }
        if (typeof (data.message) === 'string') {
          thunksApi.dispatch(
            addNotification({ status: "error", text: data.message })
          );
        }
      }
      throw Error("No response");
    } catch (error) {
      throw Error("Request is not found");
    }
  }
)
